/**
 * @fileoverview Game Over Test Suite
 *
 * Tests for all victory and draw conditions in Zodiac Chess.
 * Each test verifies that the game ends correctly with the proper result.
 *
 * Victory Conditions:
 * 1. King Race - King reaches opponent's back rank
 * 2. King Capture - Enemy king is captured
 * 3. Double Capture - When kings capture each other same turn, capturer wins
 * 4. Timeout - Player runs out of time
 *
 * Draw Conditions:
 * 1. Stalemate - Player has no legal moves
 * 2. Three-fold Repetition - Same position occurs 3 times
 * 3. Mutual Agreement - Both players agree to draw
 */

import { PieceType } from "../../zodiacEngine/types";
import { IntegrationTest, TestSuite } from "../types";

// ============================================================================
// VICTORY CONDITION TESTS
// ============================================================================

/**
 * Bear - Race victory by stepping king onto back rank
 */
export const bearRaceVictory: IntegrationTest = {
  name: "Bear race victory",
  description: "Light Bear king steps from A7 to A8 to win the race",
  inputFEN: "6BK1/8/8/8/7bk",
  lightKing: PieceType.Bear,
  darkKing: PieceType.Bear,
  move: "Light Bear A7->A8",
  expectedFEN: "7B♛/8/8/8/7b♕",
  expectedDelta: {
    pieceMoved: "Light Bear moved [A7 -> A8]",
    turnChanged: false,
    gameEnded: true,
    statusChanged: false,
    bonusTurn: false,
  },
  expectedResult: { gameEnd: "victory_race_light", winner: "light" },
};

/**
 * Crane - King capture victory
 */
export const craneKingCapture: IntegrationTest = {
  name: "Crane captures enemy king",
  description: "Light Crane king captures Dark Frog king for immediate victory",
  inputFEN: "1CK6/1fk6/8/8/8",
  lightKing: PieceType.Crane,
  darkKing: PieceType.Frog,
  move: "Light Crane A2->B2:capture",
  expectedFEN: "8/1CK6/8/8/8",
  expectedDelta: {
    pieceMoved: "Light Crane captured Dark Frog [A2 -> B2] Light Crane is now poisoned",
    statusChanged: true,
    turnChanged: false,
    bonusTurn: false,
    gameEnded: true,
  },
  expectedResult: { gameEnd: "victory_kingslayer_light", winner: "light" },
};

/**
 * Frog - Lilypad king capture
 */
export const frogLilypadKingCapture: IntegrationTest = {
  name: "Frog captures king with lilypad",
  description: "Light Frog king uses lilypad ability to capture Dark Wolf king",
  inputFEN: "FK✳️7/8/8/8/2wk5",
  lightKing: PieceType.Frog,
  darkKing: PieceType.Wolf,
  move: "Light Frog A1->E3:capture",
  expectedFEN: "8/8/8/8/2FK5",
  expectedDelta: {
    pieceMoved: "Light Frog jumped off of its Lilypad and captured Dark Wolf [A1 -> E3]",
    statusChanged: true,
    turnChanged: false,
    bonusTurn: false,
    gameEnded: true,
  },
  expectedResult: { gameEnd: "victory_kingslayer_light", winner: "light" },
};

/**
 * Fungi - Zombified piece race victory
 */
export const fungiZombieRaceVictory: IntegrationTest = {
  name: "Fungi zombie race victory",
  description: "Dark controls zombified Light king to win race for Dark",
  inputFEN: "8/8/1CK6/1uk6/8",
  lightKing: PieceType.Crane,
  darkKing: PieceType.Fungi,
  move: "Light Crane C2->D2:capture",
  expectedFEN: "8/8/1CKz6/8/8",
  isSequence: true,
  steps: [
    {
      move: "Light Crane C2->D2:capture",
      expectedFEN: "8/8/8/1CKz6/8",
      expectedDelta: {
        pieceMoved: "Light Crane captured Dark Fungi [C2 -> D2] Light Crane is now zombified",
        statusChanged: true,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Light Crane captures Fungi and becomes zombified",
    },
    {
      move: "Dark Crane D2->D1",
      expectedFEN: "8/8/8/CK7/8",
      expectedDelta: {
        pieceMoved: "Zombified Light Crane moved [D2 -> D1]",
        statusChanged: true,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "victory_zombierace_dark", winner: "dark" },
      note: "Dark controls zombified Light king to reach Dark's back rank - Dark wins!",
    },
  ],
};

export const fungiZombieRaceFailure: IntegrationTest = {
  name: "Fungi zombie race failure",
  description: "Dark controls zombified Light king to win race for Dark",
  inputFEN: "8/8/1CK6/1uk6/8",
  lightKing: PieceType.Crane,
  darkKing: PieceType.Fungi,
  move: "Light Crane C2->D2:capture",
  expectedFEN: "8/8/1CKz6/8/8",
  isSequence: true,
  steps: [
    {
      move: "Light Crane C2->D2:capture",
      expectedFEN: "8/8/8/1CKz6/8",
      expectedDelta: {
        pieceMoved: "Light Crane captured Dark Fungi [C2 -> D2] Light Crane is now zombified",
        statusChanged: true,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Light Crane captures Fungi and becomes zombified",
    },
    {
      move: "Dark Crane D2->C2",
      expectedFEN: "8/8/1CK6/8/8",
      expectedDelta: {
        pieceMoved: "Zombified Light Crane moved [D2 -> C2]",
        statusChanged: true,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "victory_kingslayer_light", winner: "light" },
      note: "Dark controls zombified Light king to accidentally not reach race and lose!",
    },
  ],
};

/**
 * Monkey consumes Fungi king - zombie race victory
 *
 * Regression test for the prezombified Monkey scenario. When a Monkey consumes
 * an enemy Fungi king, zombification is deferred to the end of the bonus move
 * (see MovementManager.ts:393-407). The game-end check must recognise the
 * pendingMonkeyZombification flag during the gap so the game continues; otherwise
 * the consuming side incorrectly wins by kingslayer before the bonus move plays.
 */
export const monkeyConsumeFungiZombieRaceVictory: IntegrationTest = {
  name: "Monkey consume Fungi king zombie race victory",
  description: "Dark Monkey consumes Light Fungi king at A7. Game must NOT end. Dark plays bonus move, Monkey becomes zombified, Light controls zombified Monkey and races to row 7 to win.",
  inputFEN: "6UK1/7m/8/8/7bk",
  lightKing: PieceType.Fungi,
  darkKing: PieceType.Bear,
  move: "Dark Monkey B8->A7:consume",
  expectedFEN: "6m1/8/8/8/7bk",
  isSequence: true,
  steps: [
    {
      move: "Dark Monkey B8->A7:consume",
      expectedFEN: "6m1/8/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Dark Monkey consumed Light Fungi [B8 -> A7] and gets a bonus turn",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: true,
        gameEnded: false,
      },
      note: "Consume the Fungi king — prezombified Monkey keeps the game alive (regression guard for the consume-king bug)",
    },
    {
      move: "Dark Monkey A7->B7",
      expectedFEN: "8/6mz1/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Dark Monkey moved [A7 -> B7]",
        statusChanged: true,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Bonus move plays as Dark; zombification applied at end of move (FEN shows mz)",
    },
    {
      move: "Light Monkey B7->B8",
      expectedFEN: "8/7m/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Zombified Dark Monkey moved [B7 -> B8]",
        statusChanged: true,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "victory_zombierace_light", winner: "light" },
      note: "Light controls zombified Monkey, reaches row 7 → Light wins by zombie race",
    },
  ],
};

/**
 * Monkey consumes Fungi king - zombie race failure (kingslayer)
 *
 * Same opening as the victory variant, but Light moves the zombified Monkey
 * away from row 7. Zombification expires → Dark wins by kingslayer.
 */
export const monkeyConsumeFungiZombieRaceFailure: IntegrationTest = {
  name: "Monkey consume Fungi king zombie race failure",
  description: "Dark Monkey consumes Light Fungi king, plays bonus, becomes zombified. Light moves zombified Monkey AWAY from row 7. Zombification expires → Dark wins by kingslayer.",
  inputFEN: "6UK1/7m/8/8/7bk",
  lightKing: PieceType.Fungi,
  darkKing: PieceType.Bear,
  move: "Dark Monkey B8->A7:consume",
  expectedFEN: "6m1/8/8/8/7bk",
  isSequence: true,
  steps: [
    {
      move: "Dark Monkey B8->A7:consume",
      expectedFEN: "6m1/8/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Dark Monkey consumed Light Fungi [B8 -> A7] and gets a bonus turn",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: true,
        gameEnded: false,
      },
      note: "Consume the Fungi king — prezombified Monkey keeps the game alive",
    },
    {
      move: "Dark Monkey A7->B7",
      expectedFEN: "8/6mz1/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Dark Monkey moved [A7 -> B7]",
        statusChanged: true,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Bonus move plays as Dark; zombification applied at end of move",
    },
    {
      move: "Light Monkey B7->A6",
      expectedFEN: "5m2/8/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Zombified Dark Monkey moved [B7 -> A6]",
        statusChanged: true,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "victory_kingslayer_dark", winner: "dark" },
      note: "Light moves zombified Monkey away from row 7 → zombification expires → Dark wins by kingslayer",
    },
  ],
};

/**
 * Fungi - Zombified Penguin race victory
 */
export const fungiZombiePenguinRace: IntegrationTest = {
  name: "Fungi zombie penguin race",
  description: "Penguin captures Fungi king, then Fungi wins as zombie Penguin",
  inputFEN: "8/8/6P→K1/6uk1/8",
  lightKing: PieceType.Penguin,
  darkKing: PieceType.Fungi,
  move: "Light Penguin C7->D7",
  expectedFEN: "8/8/8/P→K7/8",
  isSequence: true,
  steps: [
    {
      move: "Light Penguin C7->D7",
      expectedFEN: "8/8/8/6P→zK1/8",
      expectedDelta: {
        pieceMoved: "Light Penguin captured Dark Fungi [C7 -> D7] Light Penguin is now zombified",
        statusChanged: true,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Penguin does not get a rotate when it becomes zombified! Special rule!!",
    },
    {
      move: "Dark Penguin D7->D6:rotate",
      expectedFEN: "8/8/8/6P↓zK1/8",
      expectedDelta: {
        pieceMoved: "Zombified Light Penguin rotated down in place [D7]",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: true,
        gameEnded: false,
      },
      note: "Zombie penguin completes reverse turn option of turn then move!",
    },
    {
      move: "Dark Penguin D7->D1",
      expectedFEN: "8/8/8/P↓K7/8",
      expectedDelta: {
        pieceMoved: "Zombified Light Penguin slid [D7 -> D1]",
        statusChanged: true,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "victory_zombierace_dark", winner: "dark" },    
      note: "zombie penguin acting as zombie fungi king wins by race!",
    },
  ],
};

/**
 * Goat - Stalemate on goat stood condition
 */
export const goatStalemate: IntegrationTest = {
  name: "Goat stood stalemate",
  description: "Light Goat faints, then Dark Bear steps on fainted Goat causing stalemate",
  inputFEN: "GK7/1b6/8/8/7ck",
  lightKing: PieceType.Goat,
  darkKing: PieceType.Crane,
  move: "Light Goat A1->A1",
  expectedFEN: "G$K+b7/8/8/8/7ck",
  isSequence: true,
  steps: [
    {
      move: "Light Goat A1->A1",
      expectedFEN: "G$K7/1b6/8/8/7ck",
      expectedDelta: {
        pieceMoved: "Light Goat fainted [A1]",
        statusChanged: true,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Goat has no legal moves and faints",
    },
    {
      move: "Dark Bear B2->A1",
      expectedFEN: "G$K+b7/8/8/8/7ck",
      expectedDelta: {
        pieceMoved: "Dark Bear stands on fainted Light Goat [B2 -> A1]",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "draw_stalemate" },
      note: "Bear steps on fainted Goat - Light king has no legal moves, draw by stalemate",
    },
  ],
};

/**
 * Monkey - Swing bonus turn to race victory
 */
export const monkeySwingBonusTurnRace: IntegrationTest = {
  name: "Monkey swing bonus turn race victory",
  description: "Light Monkey swings from tree on A7, gets bonus turn, then wins race on A8",
  inputFEN: "8/8/4MKT2/8/7ck",
  lightKing: PieceType.Monkey,
  darkKing: PieceType.Crane,
  move: "Light Monkey C5->C7",
  expectedFEN: "8/8/5TMK1/8/7ck",
  isSequence: true,
  steps: [
    {
      move: "Light Monkey C5->C7",
      expectedFEN: "8/8/5TMK1/8/7ck",
      expectedDelta: {
        pieceMoved: "Light Monkey swung over Light Tree [C5 -> C7] Light Monkey gets a bonus turn",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: true,
        gameEnded: false,
      },
      note: "Monkey swings over tree, gets bonus turn",
    },
    {
      move: "Light Monkey C7->C8",
      expectedFEN: "8/8/5T1MK/8/7ck",
      expectedDelta: {
        pieceMoved: "Light Monkey moved [C7 -> C8]",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "victory_race_light", winner: "light" },
      note: "Monkey uses bonus turn to complete race - game ends immediately",
    },
  ],
};

/**
 * Octopus - Entangle last enemy for stalemate
 */
export const octopusEntangleStalemate: IntegrationTest = {
  name: "Octopus entangle stalemate",
  description: "Light Octopus entangles Dark's only piece, resulting in stalemate draw",
  inputFEN: "OK7/8/2ck5/8/8/8",
  lightKing: PieceType.Octopus,
  darkKing: PieceType.Crane,
  move: "Light Octopus A1->C3:entangle",
  expectedFEN: "8/1OK6/2c⛓k5/8/8",
  expectedDelta: {
    pieceMoved: "Light Octopus moved [A1 -> B2] and Entangled Dark Crane [C3]",
    statusChanged: true,
    turnChanged: false,
    bonusTurn: false,
    gameEnded: true,
  },
  expectedResult: { gameEnd: "draw_stalemate" },
};

/**
 * Penguin - Race victory before bonus turn rotate
 */
export const penguinRaceBeforeBonusTurn: IntegrationTest = {
  name: "Penguin race victory before bonus turn",
  description: "Penguin wins race on A8, game ends immediately without bonus turn",
  inputFEN: "8/P↑K7/8/8/7ck",
  lightKing: PieceType.Penguin,
  darkKing: PieceType.Crane,
  move: "Light Penguin B1->B8",
  expectedFEN: "8/7P↑♛/8/8/7ck",
  expectedDelta: {
    pieceMoved: "Light Penguin slid [B1 -> B8]",
    statusChanged: false,
    turnChanged: false,
    bonusTurn: false, // Game ends, so no bonus turn
    gameEnded: true,
  },
  expectedResult: { gameEnd: "victory_race_light", winner: "light" },
};

/**
 * Rhino - Triple capture king slayer
 */
export const rhinoTripleCaptureKingSlayer: IntegrationTest = {
  name: "Rhino mega triple capture kingslayer",
  description: "Light Rhino tramples through 3 pieces including enemy king",
  inputFEN: "8/R*Kmbfk4/8/8/8",
  lightKing: PieceType.Rhino,
  darkKing: PieceType.Frog,
  move: "Light Rhino B1->B4:trample",
  expectedFEN: "8/3RK4/8/8/8",
  expectedDelta: {
    pieceMoved: "Light Rhino trampled Dark Monkey [B2], Dark Bear [B3] and Dark Frog [B4] [B1 -> B4] The Light Rhino is now poisoned",
    statusChanged: true,
    turnChanged: false,
    bonusTurn: false,
    gameEnded: true,
  },
  expectedResult: { gameEnd: "victory_kingslayer_light", winner: "light" },
};

/**
 * Snake - Timeout test
 */
export const snakeTimeout: IntegrationTest = {
  name: "Snake timeout loss",
  description: "Light Snake runs out of time and loses",
  inputFEN: "S*K7/8/8/8/7ck", // Letter format for input (K/k for kings, * for snake unused ability)
  lightKing: PieceType.Snake,
  darkKing: PieceType.Crane,
  move: "timeout:light",
  expectedFEN: "S✳️♛7/8/8/8/7c♕", // Symbol format for output (♛/♕ for kings, ✳️ for unused ability)
  expectedDelta: {
    pieceMoved: "Light ran out of time",
    statusChanged: false,
    turnChanged: false,
    bonusTurn: false,
    gameEnded: true,
  },
  expectedResult: { gameEnd: "victory_timeout_dark", winner: "dark" },
};

/**
 * Snake - Failed shed respawn kingslayer
 * Snake king has already used shed ability (✳️), so capture is fatal
 */
export const snakeShedRespawnKingslayer: IntegrationTest = {
  name: "Snake failed shed respawn kingslayer",
  description: "Dark Snake king is captured, already used shed ability, game over",
  inputFEN: "BKs*k5c/8/8/8/8",
  lightKing: PieceType.Bear,
  darkKing: PieceType.Snake,
  move: "Light Bear A1->A2:capture",
  expectedFEN: "1BK5c/8/8/8/8",
  expectedDelta: {
    pieceMoved: "Light Bear captured Dark Snake [A1 -> A2]",
    statusChanged: true, // Snake's shed ability (✳️) removed on death
    turnChanged: false,
    bonusTurn: false,
    gameEnded: true,
  },
  expectedResult: { gameEnd: "victory_kingslayer_light", winner: "light" },
};

/**
 * Tree - Meadow draw condition
 */
export const treeMeadowStalemate: IntegrationTest = {
  name: "Tree meadow stalemate",
  description: "Dark Tree meadowed, Dark has no legal moves",
  inputFEN: "8/8/UK1tk5/8/8",
  lightKing: PieceType.Fungi,
  darkKing: PieceType.Tree,
  move: "Light Fungi C1->C2",
  expectedFEN: "8/8/1U♛t⚘♕5/8/8",
  expectedDelta: {
    pieceMoved: "Light Fungi moved [C1 -> C2] Dark Tree is now meadowed",
    statusChanged: true,
    turnChanged: false,
    bonusTurn: false,
    gameEnded: true,
  },
  expectedResult: { gameEnd: "draw_stalemate" },
};

/**
 * Wolf - Three-fold repetition draw
 */
export const bearThreeFoldRepetition: IntegrationTest = {
  name: "Bear three-fold repetition draw",
  description: "Same position occurs 3 times, resulting in draw",
  inputFEN: "BK7/8/8/8/7bk",
  lightKing: PieceType.Bear,
  darkKing: PieceType.Bear,
  move: "Light Bear A1->B1",
  expectedFEN: "8/BK7/8/8/7bk",
  isSequence: true,
  steps: [
    {
      move: "Light Bear A1->B1",
      expectedFEN: "8/BK7/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Light Bear moved [A1 -> B1]",
        statusChanged: false,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Position 1",
    },
    {
      move: "Dark Bear E8->D8",
      expectedFEN: "8/BK7/8/7bk/8",
      expectedDelta: {
        pieceMoved: "Dark Bear moved [E8 -> D8]",
        statusChanged: false,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Position 2",
    },
    {
      move: "Light Bear B1->A1",
      expectedFEN: "BK7/8/8/7bk/8",
      expectedDelta: {
        pieceMoved: "Light Bear moved [B1 -> A1]",
        statusChanged: false,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Back to position 1 (first repetition)",
    },
    {
      move: "Dark Bear D8->E8",
      expectedFEN: "BK7/8/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Dark Bear moved [D8 -> E8]",
        statusChanged: false,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Back to starting position (second repetition)",
    },
    {
      move: "Light Bear A1->B1",
      expectedFEN: "8/BK7/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Light Bear moved [A1 -> B1]",
        statusChanged: false,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Position 1 again",
    },
    {
      move: "Dark Bear E8->D8",
      expectedFEN: "8/BK7/8/7bk/8",
      expectedDelta: {
        pieceMoved: "Dark Bear moved [E8 -> D8]",
        statusChanged: false,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Position 2 again",
    },
    {
      move: "Light Bear B1->A1",
      expectedFEN: "BK7/8/8/7bk/8",
      expectedDelta: {
        pieceMoved: "Light Bear moved [B1 -> A1]",
        statusChanged: false,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
    },
    {
      move: "Dark Bear D8->E8",
      expectedFEN: "BK7/8/8/8/7bk",
      expectedDelta: {
        pieceMoved: "Dark Bear moved [D8 -> E8]",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "draw_repetition" },
      note: "Third repetition of position - automatic draw",
    },
  ],
};

/**
 * Virus - Bonus turn kingslayer for instant game over
 */
export const virusBonusTurnKingslayer: IntegrationTest = {
  name: "Virus bonus turn kingslayer",
  description: "Light Virus gets bonus turn and captures enemy king on bonus turn",
  inputFEN: "BK7/V7/8/b1ck1f3/8",
  lightKing: PieceType.Virus,
  darkKing: PieceType.Crane,
  move: "Light Virus B1->D1:infect",
  expectedFEN: "BK7/8/8/b+V1ck1f3/8",
  isSequence: true,
  steps: [
    {
      move: "Light Virus B1->D1:infect",
      expectedFEN: "BK7/8/8/b+V1c♕1f3/8",
      expectedDelta: {
        pieceMoved: "Light Virus infected Dark Bear [B1 -> D1]",
        statusChanged: true,
        turnChanged: false,
        bonusTurn: true,
        gameEnded: false,
      },
      note: "Virus infects Dark bear, gets bonus turn",
    },
    {
      move: "Light Virus D1->D3:infect",
      expectedFEN: "BK7/8/8/2c♕+V1f3/8",
      expectedDelta: {
        pieceMoved: "Light Virus infected Dark Crane [D1 -> D3] Light Virus captured Dark Bear [D1]",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: true,
        gameEnded: false,
      },
      note: "Virus infects Dark Crane king (but doesn't capture yet), still has bonus turn",
    },
    {
      move: "Light Virus D3->D5:infect",
      expectedFEN: "BK7/8/8/4f+V3/8",
      expectedDelta: {
        pieceMoved: "Light Virus infected Dark Frog [D3 -> D5] Light Virus captured Dark Crane [D3]",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "victory_kingslayer_light", winner: "light" },
      note: "Virus leaves infected Crane on bonus turn, capturing it - immediate game end",
    },
  ],
};

/**
 * Rhino - Zombie rhino reverse charge
 * Zombified Rhino can charge BACKWARDS (towards its original back rank)
 * because Dark controls it and the charge direction is reversed
 */
export const rhinoZombieReverseCharge: IntegrationTest = {
  name: "Zombie rhino reverse charge",
  description: "Light Rhino captures Fungi, becomes zombified, then Dark uses zombie rhino to charge backwards to Light's back rank",
  inputFEN: "8/8/BR*K1uk4/8/8",
  lightKing: PieceType.Rhino,
  darkKing: PieceType.Fungi,
  move: "Light Rhino C2->C4:trample",
  expectedFEN: "8/8/B2RKz4/8/8",
  isSequence: true,
  steps: [
    {
      move: "Light Rhino C2->C4:trample",
      expectedFEN: "8/8/B2Rz*K4/8/8",
      expectedDelta: {
        pieceMoved: "Light Rhino trampled Dark Fungi [C4] [C2 -> C4] Light Rhino is now zombified",
        statusChanged: true,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Rhino charges forward to capture Fungi king, becomes zombified",
    },
    {
      move: "Dark Rhino C4->C1:trample",
      expectedFEN: "8/8/RK7/8/8",
      expectedDelta: {
        pieceMoved: "Zombified Light Rhino trampled Light Bear [C1] [C4 -> C1]",
        statusChanged: true,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "victory_zombierace_dark", winner: "dark" },
      note: "Dark controls zombie Rhino to charge BACKWARDS (reversed direction). Captures Bear and reaches Light's back rank - Dark wins by zombie race!",
    },
  ],
};

export const rhinoZombieReverseReverse: IntegrationTest = {
  name: "Zombie rhino zombify then revert",
  description: "Light Rhino captures Fungi, becomes zombified, Dark moves it, then Light regains control after zombification ends",
  inputFEN: "8/8/1R*K1u4/8/7bk",
  lightKing: PieceType.Rhino,
  darkKing: PieceType.Bear,
  move: "Light Rhino C2->C4:trample",
  expectedFEN: "8/8/3RKz4/8/8",
  isSequence: true,
  steps: [
    {
      move: "Light Rhino C2->C4:trample",
      expectedFEN: "8/8/3Rz*K4/8/7bk",
      expectedDelta: {
        pieceMoved: "Light Rhino trampled Dark Fungi [C4] [C2 -> C4] Light Rhino is now zombified",
        statusChanged: true,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Rhino charges forward to capture Fungi, becomes zombified",
    },
    {
      move: "Dark Rhino C4->C2:trample",
      expectedFEN: "8/8/1RK6/8/7bk",
      expectedDelta: {
        pieceMoved: "Zombified Light Rhino moved [C4 -> C2]",
        statusChanged: true,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Dark controls zombie Rhino to move backwards. No race win since C2 is not back rank (C1 would be).",
    },
    {
      move: "Light Rhino C2->C4:trample",
      expectedFEN: "8/8/3R*K4/8/7bk",
      expectedDelta: {
        pieceMoved: "Light Rhino moved [C2 -> C4]",
        statusChanged: false,
        turnChanged: true,
        bonusTurn: false,
        gameEnded: false,
      },
      note: "Light rhino can move back again after zombification and the UI and status revert!",
    },
  ],
};

export const monkeySwingfungiwolf: IntegrationTest = {
  name: "Monkey swing consume and king capture double bonus",
  description: "Light Monkey swings from tree, consumes fungi for bonus turn, then captures dark wolf king",
  inputFEN: "8/8/MK3u3/1T1wk4/8",
  lightKing: PieceType.Monkey,
  darkKing: PieceType.Wolf,
  move: "Light Monkey C1 -> E3:swing",
  expectedFEN: "8/8/4u3/1T1wk4/2MK5",
  isSequence: true,
  steps: [
    {
      move: "Light Monkey C1 -> E3:swing",
      expectedFEN: "8/8/4u3/1T1wk4/2MK5",
      expectedDelta: {
        pieceMoved: "Light Monkey swung over Light Tree [C1 -> E3] Light Monkey gets a bonus turn",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: true,
        gameEnded: false,
      },
      note: "Monkey gets a bonus turn from swing",
    },
    {
      move: "Light Monkey E3 -> C5",
      expectedFEN: "8/8/4MK3/1T1wk4/8",
      expectedDelta: {
        pieceMoved: "Light Monkey leaped over Dark Wolf and consumed Dark Fungi [E3 -> C5] Light Monkey gets a bonus turn",
        statusChanged: false,
        turnChanged: false,
        bonusTurn: true,
        gameEnded: false,
      },
      note: "Monkey leaps dark wolf and consumes fungi for another bonus turn!",
    },
    {
      move: "Light Monkey C5 -> D4:capture",
      expectedFEN: "8/8/8/1T1Mz♛4/8",
      expectedDelta: {
        pieceMoved: "Light Monkey captured Dark Wolf [C5 -> D4] Light Monkey is now zombified",
        statusChanged: true,
        turnChanged: false,
        bonusTurn: false,
        gameEnded: true,
      },
      expectedResult: { gameEnd: "victory_kingslayer_light", winner: "light" },
      note: "After 2 bonus turns the Monkey attacks the enemy wolf king and captures it - ending the game with zombification applied",
    },
  ],
  expectedResult: { gameEnd: "victory_kingslayer_light", winner: "light" },
};

// ============================================================================
// EXPORT TEST SUITE
// ============================================================================

export const gameOverSuite: TestSuite = {
  name: "Game Over",
  description: "Tests for all victory and draw conditions - race victories, king captures, timeouts, stalemates, and repetition draws",
  tests: [
    bearRaceVictory,
    craneKingCapture,
    frogLilypadKingCapture,
    fungiZombieRaceVictory,
    fungiZombieRaceFailure,
    monkeyConsumeFungiZombieRaceVictory,
    monkeyConsumeFungiZombieRaceFailure,
    fungiZombiePenguinRace,
    goatStalemate,
    monkeySwingBonusTurnRace,
    octopusEntangleStalemate,
    penguinRaceBeforeBonusTurn,
    rhinoTripleCaptureKingSlayer,
    rhinoZombieReverseCharge,
    rhinoZombieReverseReverse,
    snakeTimeout,
    snakeShedRespawnKingslayer,
    treeMeadowStalemate,
    bearThreeFoldRepetition,
    virusBonusTurnKingslayer,
    monkeySwingfungiwolf,
  ],
};
